import StickyTableResponsive from '../components/common/StickyTableResponsive';
import SortableTh from '../components/common/SortableTh';
import React, { useEffect, useState } from 'react';
import { useTableSort } from '../hooks/useTableSort';
import { useNavigate } from 'react-router-dom';
import MasterLayout from '../masterLayout/MasterLayout';
import Breadcrumb from '../components/Breadcrumb';
import {
  AppButton,
  AppCard,
  AppEmptyState,
  AppPagination,
  AppSpinner,
  AppStatusBadge,
} from '../components/ui';
import useAppConfirm from '../hooks/useAppConfirm';
import { notify } from '../components/common/AppToast';
import apiService from '../services/apiService';
import { UserRoleBadge } from '../components/admin/UserRolesPicker';
import UserRolesManageModal from '../components/admin/UserRolesManageModal';
import { getDisplayRoleLabels, getDisplayRoles } from '../config/userRoles';
import useUserRoles from '../hooks/useUserRoles';
import { ROUTES } from '../config/routes';
import '../styles/kkp-planning-page.scss';

const TABLE_SORT_COLUMNS = {
  utilisateur: { type: 'string', getValue: (row) => `${row.firstName} ${row.lastName}` },
  email: { type: 'string', getValue: (row) => row.email },
  role: { type: 'string', getValue: (row) => getDisplayRoleLabels(row.roles) },
  statut: { type: 'string', getValue: (row) => (row.isActive ? 'actif' : 'inactif') },
  jetons: { type: 'number', getValue: (row) => row.jetonsActifs ?? 0 },
  createdAt: { type: 'date', getValue: (row) => row.createdAt },
};

const UserPage = () => {
  const { ask, modal } = useAppConfirm();
  const navigate = useNavigate();
  const { roles, loading: rolesLoading, reload: reloadRoles } = useUserRoles();
  const [rolesModalOpen, setRolesModalOpen] = useState(false);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    pages: 0,
  });

  const { sortedRows, sort, toggleSort, getSortIcon } = useTableSort(users, TABLE_SORT_COLUMNS);

  const loadUsers = async (page = 1) => {
    try {
      setLoading(true);
      const result = await apiService.makeRequest('GET', '/users', null, {
        page,
        limit: 20,
      });

      if (result.success) {
        setUsers(result.data);
        setPagination(result.pagination);
      } else {
        throw new Error(result.message || 'Erreur lors du chargement des utilisateurs');
      }
    } catch (err) {
      notify.error(err.message || 'Erreur lors du chargement des utilisateurs');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadUsers();
  }, []);

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const handleDeleteUser = async (userId) => {
    const ok = await ask({
      title: 'Supprimer cet utilisateur ?',
      message: 'Êtes-vous sûr de vouloir supprimer cet utilisateur ?',
      confirmLabel: 'Supprimer',
      confirmVariant: 'danger',
    });
    if (!ok) return;

    try {
      const result = await apiService.makeRequest('DELETE', `/users/${userId}`);
      if (result.success) {
        notify.success('Utilisateur supprimé');
        loadUsers(pagination.page);
      } else {
        throw new Error(result.message || 'Erreur lors de la suppression');
      }
    } catch (err) {
      notify.error(err.message || 'Erreur lors de la suppression de l\'utilisateur');
    }
  };

  const rangeStart = ((pagination.page - 1) * pagination.limit) + 1;
  const rangeEnd = Math.min(pagination.page * pagination.limit, pagination.total);

  return (
    <MasterLayout>
      <Breadcrumb
        title="Utilisateurs"
        subtitle="Administration des comptes utilisateurs"
        actions={(
          <div className="d-flex gap-2">
            <AppButton
              size="sm"
              variant="outline-secondary"
              icon="mdi:account-key-outline"
              onClick={() => setRolesModalOpen(true)}
            >
              Gérer les rôles
            </AppButton>
            <AppButton
              size="sm"
              icon="mdi:plus"
              onClick={() => navigate(ROUTES.admin.usersAdd)}
            >
              Nouvel utilisateur
            </AppButton>
          </div>
        )}
      />

      <AppCard
        className="kkp-planning-results-card"
        bodyClassName="p-0"
        footer={pagination.pages > 1 ? (
          <AppPagination
            variant="simple"
            pagination={pagination}
            totalLabel={`${rangeStart}–${rangeEnd} sur ${pagination.total}`}
            onPageChange={loadUsers}
          />
        ) : null}
      >
        {loading ? (
          <AppSpinner />
        ) : sortedRows.length === 0 ? (
          <AppEmptyState message="Aucun utilisateur trouvé" />
        ) : (
          <StickyTableResponsive>
            <table className="table table-hover mb-0 align-middle">
              <thead>
                <tr>
                  <SortableTh sortKey="utilisateur" label="Utilisateur" sort={sort} onSort={toggleSort} getSortIcon={getSortIcon} />
                  <SortableTh sortKey="email" label="Email" sort={sort} onSort={toggleSort} getSortIcon={getSortIcon} />
                  <SortableTh sortKey="role" label="Rôles" sort={sort} onSort={toggleSort} getSortIcon={getSortIcon} />
                  <SortableTh sortKey="statut" label="Statut" sort={sort} onSort={toggleSort} getSortIcon={getSortIcon} />
                  <SortableTh sortKey="jetons" label="Jetons" sort={sort} onSort={toggleSort} getSortIcon={getSortIcon} />
                  <SortableTh sortKey="createdAt" label="Création" sort={sort} onSort={toggleSort} getSortIcon={getSortIcon} />
                  <SortableTh label="Actions" style={{ width: 120 }} sortable={false} />
                </tr>
              </thead>
              <tbody>
                {sortedRows.map((user) => (
                  <tr key={user.id}>
                    <td>
                      <div className="d-flex align-items-center gap-2">
                        <div className="w-32-px h-32-px bg-primary-600 text-white rounded-circle d-flex align-items-center justify-content-center text-xs fw-semibold">
                          {(user.firstName?.[0] || '')}{(user.lastName?.[0] || '')}
                        </div>
                        <span className="fw-medium">{user.firstName} {user.lastName}</span>
                      </div>
                    </td>
                    <td className="text-secondary-light">{user.email}</td>
                    <td>
                      <div className="d-flex flex-wrap gap-1">
                        {getDisplayRoles(user.roles).map((role) => (
                          <UserRoleBadge key={role} role={role} />
                        ))}
                      </div>
                    </td>
                    <td>
                      <AppStatusBadge
                        pill
                        status={user.isActive ? 'active' : 'invalid'}
                        label={user.isActive ? 'Actif' : 'Inactif'}
                      />
                    </td>
                    <td className="text-secondary-light">{user.jetonsActifs ?? 0}</td>
                    <td className="text-secondary-light text-sm">{formatDate(user.createdAt)}</td>
                    <td>
                      <div className="d-flex gap-1">
                        <AppButton
                          size="sm"
                          variant="outline-primary"
                          icon="mdi:pencil-outline"
                          title="Modifier"
                          onClick={() => navigate(ROUTES.admin.usersEdit(user.id))}
                        />
                        <AppButton
                          size="sm"
                          variant="outline-danger"
                          icon="mdi:trash-can-outline"
                          title="Supprimer"
                          onClick={() => handleDeleteUser(user.id)}
                        />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </StickyTableResponsive>
        )}
      </AppCard>
      <UserRolesManageModal
        show={rolesModalOpen}
        roles={roles}
        loading={rolesLoading}
        onClose={() => setRolesModalOpen(false)}
        onChanged={reloadRoles}
      />
      {modal}
    </MasterLayout>
  );
};

export default UserPage;
